import * as THREE from 'three'
import Experience from '../Experience.js'

/** Pushes anchor past knuckle row toward palm hollow (fraction of wrist→initial estimate). */
const PALM_CENTER_OUT_BIAS = 0.5
/** Wrist → middle metacarpal when knuckle joints are missing. */
const FALLBACK_WRIST_TO_MIDDLE_T = 0.94
/** Blend knuckle centroid toward middle PIP (deeper in the open palm). */
const BLEND_TO_MIDDLE_PROXIMAL = 0.28

/** Nudge anchor along palm outward normal so content sits visibly above skin (metres). */
const PALM_SURFACE_HOVER_M = 0.022

export default class HandTracking {
    constructor() {
        this.experience = new Experience()
        this.renderer = this.experience.renderer
        this.scene = this.experience.scene

        // Hand references
        this.handLeft = null
        this.handRight = null

        // Palm positions (for model placement)
        this.leftPalmPosition = new THREE.Vector3()
        this.rightPalmPosition = new THREE.Vector3()
        this.leftPalmQuaternion = new THREE.Quaternion()
        this.rightPalmQuaternion = new THREE.Quaternion()

        this._wristToRoughPalm = new THREE.Vector3()
        this._palmUa = new THREE.Vector3()
        this._palmUb = new THREE.Vector3()
        this._palmHoverNormal = new THREE.Vector3()
        this._palmRefDir = new THREE.Vector3()

        // Tracking state
        this.isLeftHandTracked = false
        this.isRightHandTracked = false

        // Pinch detection
        this.leftPinchActive = false
        this.rightPinchActive = false
        this.pinchDistance = new THREE.Vector3()

        // Scale gesture
        this.isScaling = false
        this.initialPinchDistance = 0
        this.currentScale = 1

        this.setupHandTracking()
    }

    setupHandTracking() {
        // Get hand controllers (index 0 = right, index 1 = left)
        this.handRight = this.renderer.instance.xr.getHand(0)
        this.handLeft = this.renderer.instance.xr.getHand(1)

        // Optional: Add visual hand models for debugging
        // Uncomment these if you want to see hand meshes
        // const handModelFactory = new XRHandModelFactory()
        // this.handRight.add(handModelFactory.createHandModel(this.handRight))
        // this.handLeft.add(handModelFactory.createHandModel(this.handLeft))
        // this.scene.add(this.handRight, this.handLeft)
    }

    update() {
        // Only update hand tracking when in XR mode
        if (!this.renderer.instance.xr.isPresenting) {
            this.isLeftHandTracked = false
            this.isRightHandTracked = false
            return
        }

        this.updateHandPositions()
        this.updatePinchDetection()
        this.updateScaleGesture()
    }

    /**
     * Nudges a rough palm point further from the wrist along the same direction (into the open hand).
     */
    _applyPalmCenterShift(position, wrist) {
        if (!wrist) return
        this._wristToRoughPalm.copy(position).sub(wrist.position)
        position.addScaledVector(this._wristToRoughPalm, PALM_CENTER_OUT_BIAS)
    }

    /**
     * Palm plane outward direction (same convention as PALM_SURFACE_HOVER_M).
     * @returns {boolean}
     */
    _fillPalmHoverNormal(j, wrist, middleM, out) {
        const indexM = j['index-finger-metacarpal']
        const pinkyM = j['pinky-finger-metacarpal'] ?? j['little-finger-metacarpal']

        if (wrist && indexM && pinkyM) {
            this._palmUa.subVectors(indexM.position, wrist.position)
            this._palmUb.subVectors(pinkyM.position, wrist.position)
            out.crossVectors(this._palmUa, this._palmUb)

            if (out.lengthSq() < 1e-10) {
                if (!middleM) return false
                out.set(0, 1, 0).applyQuaternion(middleM.quaternion)
            } else {
                out.normalize()
                if (middleM) {
                    this._palmRefDir.subVectors(middleM.position, wrist.position)
                } else {
                    this._palmRefDir.copy(this._palmUa)
                }
                if (out.dot(this._palmRefDir) < 0) out.negate()
            }
            return true
        }

        if (middleM) {
            out.set(0, 1, 0).applyQuaternion(middleM.quaternion)
            return true
        }
        return false
    }

    /** Shift palm anchor slightly out of the hand plane toward the palm “top” opening. */
    _hoverAbovePalmSurface(position, j, wrist, middleM) {
        if (this._fillPalmHoverNormal(j, wrist, middleM, this._palmHoverNormal)) {
            position.addScaledVector(this._palmHoverNormal, PALM_SURFACE_HOVER_M)
        }
    }

    /**
     * Palm anchor: centroid of knuckle metacarpals + blend toward middle proximal phalanx + shift into palm.
     * Falls back to wrist → middle metacarpal with the same shift when knuckles are incomplete.
     */
    _updatePalmFromHand(hand, position, quaternion) {
        if (!hand?.joints) return false

        const j = hand.joints
        const knuckleMetacarpals = [
            j['index-finger-metacarpal'],
            j['middle-finger-metacarpal'],
            j['ring-finger-metacarpal'],
            j['pinky-finger-metacarpal'],
            j['little-finger-metacarpal'],
        ].filter((joint) => joint != null)

        const wrist = j['wrist']
        const middleM = j['middle-finger-metacarpal']

        if (knuckleMetacarpals.length >= 3) {
            position.set(0, 0, 0)
            for (const joint of knuckleMetacarpals) {
                position.add(joint.position)
            }
            position.multiplyScalar(1 / knuckleMetacarpals.length)

            const midProx = j['middle-finger-phalanx-proximal']
            if (midProx) {
                position.lerp(midProx.position, BLEND_TO_MIDDLE_PROXIMAL)
            }

            this._applyPalmCenterShift(position, wrist)
            this._hoverAbovePalmSurface(position, j, wrist, middleM)

            quaternion.copy(middleM.quaternion)
            return true
        }

        if (wrist && middleM) {
            position.copy(wrist.position).lerp(middleM.position, FALLBACK_WRIST_TO_MIDDLE_T)
            this._applyPalmCenterShift(position, wrist)
            this._hoverAbovePalmSurface(position, j, wrist, middleM)

            quaternion.copy(middleM.quaternion)
            return true
        }

        return false
    }

    /**
     * World-space direction from palm plane toward the open-hand side; for offsetting the XR rig with hand rotation.
     * @param {THREE.Vector3} out
     * @returns {boolean}
     */
    getPrimaryPalmOutwardDirection(out) {
        const hand = this.isRightHandTracked
            ? this.handRight
            : this.isLeftHandTracked
              ? this.handLeft
              : null
        if (!hand?.joints) return false
        const j = hand.joints
        return this._fillPalmHoverNormal(j, j['wrist'], j['middle-finger-metacarpal'], out)
    }

    updateHandPositions() {
        this.isLeftHandTracked = this._updatePalmFromHand(
            this.handLeft,
            this.leftPalmPosition,
            this.leftPalmQuaternion
        )
        this.isRightHandTracked = this._updatePalmFromHand(
            this.handRight,
            this.rightPalmPosition,
            this.rightPalmQuaternion
        )
    }

    updatePinchDetection() {
        const pinchThreshold = 0.1 // 10cm

        // Left hand pinch
        if (this.handLeft?.joints) {
            const thumbTip = this.handLeft.joints['thumb-tip']
            const indexTip = this.handLeft.joints['index-finger-tip']

            if (thumbTip && indexTip) {
                const distance = thumbTip.position.distanceTo(indexTip.position)
                this.leftPinchActive = distance < pinchThreshold
            } else {
                this.leftPinchActive = false
            }
        } else {
            this.leftPinchActive = false
        }

        // Right hand pinch
        if (this.handRight?.joints) {
            const thumbTip = this.handRight.joints['thumb-tip']
            const indexTip = this.handRight.joints['index-finger-tip']

            if (thumbTip && indexTip) {
                const distance = thumbTip.position.distanceTo(indexTip.position)
                this.rightPinchActive = distance < pinchThreshold
            } else {
                this.rightPinchActive = false
            }
        } else {
            this.rightPinchActive = false
        }
    }

    updateScaleGesture() {
        // Two-hand pinch for scaling
        if (this.leftPinchActive && this.rightPinchActive) {
            const leftThumb = this.handLeft.joints['thumb-tip']
            const rightThumb = this.handRight.joints['thumb-tip']

            if (leftThumb && rightThumb) {
                const currentDistance = leftThumb.position.distanceTo(rightThumb.position)

                if (!this.isScaling) {
                    // Start scaling gesture
                    this.isScaling = true
                    this.initialPinchDistance = currentDistance
                } else {
                    // Update scale based on distance change
                    this.currentScale = currentDistance / this.initialPinchDistance
                    // Clamp scale between 0.25x and 0.5x
                    this.currentScale = THREE.MathUtils.clamp(this.currentScale, 0.25, 0.5)
                }
            }
        } else {
            if (this.isScaling) {
                // End scaling gesture - keep the final scale
                this.isScaling = false
            }
        }
    }

    // Utility method to get the primary hand palm position (right hand preferred)
    getPrimaryPalmPosition() {
        if (this.isRightHandTracked) {
            return this.rightPalmPosition.clone()
        } else if (this.isLeftHandTracked) {
            return this.leftPalmPosition.clone()
        }
        return null
    }

    getPrimaryPalmQuaternion() {
        if (this.isRightHandTracked) {
            return this.rightPalmQuaternion.clone()
        } else if (this.isLeftHandTracked) {
            return this.leftPalmQuaternion.clone()
        }
        return null
    }

    isAnyHandTracked() {
        return this.isLeftHandTracked || this.isRightHandTracked
    }

    getScale() {
        return this.currentScale
    }
}
